import React, { useState, useEffect } from 'react';
import { Sparkles, TrendingUp, AlertCircle, Lightbulb, RefreshCw } from 'lucide-react';
import { getComponentSuggestions, getLayoutRecommendations } from '../../../services/aiService';

const AISuggestionsPanel = ({ components, onAddComponent, pageContext }) => {
  const [suggestions, setSuggestions] = useState(null);
  const [layoutRecommendations, setLayoutRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('components');

  useEffect(() => {
    if (components?.length >= 0) {
      loadSuggestions();
    }
  }, [components?.length, pageContext]);

  const loadSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [suggestionsData, layoutData] = await Promise.all([
        getComponentSuggestions(components, pageContext),
        components?.length > 0 ? getLayoutRecommendations(components, { width: 1200, height: 600 }) : null
      ]);
      setSuggestions(suggestionsData);
      setLayoutRecommendations(layoutData);
    } catch (err) {
      setError(err?.message || 'Failed to load AI suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuggestedComponent = (suggestion) => {
    if (onAddComponent) {
      onAddComponent({
        type: suggestion?.componentType,
        name: suggestion?.componentName,
        id: `${suggestion?.componentType?.toLowerCase()}-${Date.now()}`,
        icon: suggestion?.componentType,
        description: suggestion?.reasoning
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'medium': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">AI Design Assistant</h3>
          </div>
          <button
            onClick={loadSuggestions}
            disabled={loading}
            className="p-1.5 rounded-lg text-gray-600 hover:bg-white hover:text-blue-600 transition-colors disabled:opacity-50"
            aria-label="Refresh suggestions"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-shrink-0 flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('components')}
          className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'components' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Components
        </button>
        <button
          onClick={() => setActiveTab('layout')}
          className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
            activeTab === 'layout' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          Layout
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mb-3" />
            <p className="text-sm text-gray-600">Analyzing your design...</p>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && activeTab === 'components' && suggestions && (
          <div className="space-y-4">
            {/* Overall Recommendation */}
            {suggestions?.overallRecommendation && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">Design Insight</p>
                    <p className="text-sm text-blue-800">{suggestions?.overallRecommendation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Component Suggestions */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Suggested Components
              </h4>
              {suggestions?.suggestions?.map((suggestion, index) => (
                <div
                  key={index}
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h5 className="text-sm font-semibold text-gray-900">
                          {suggestion?.componentName}
                        </h5>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getPriorityColor(suggestion?.priority)}`}>
                          {suggestion?.priority}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">{suggestion?.componentType}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{suggestion?.reasoning}</p>
                  {suggestion?.placement && (
                    <p className="text-xs text-gray-500 mb-3">
                      <span className="font-medium">Placement:</span> {suggestion?.placement}
                    </p>
                  )}
                  <button
                    onClick={() => handleAddSuggestedComponent(suggestion)}
                    className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Add to Canvas
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && !error && activeTab === 'layout' && layoutRecommendations && (
          <div className="space-y-4">
            {/* Layout Score */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">Layout Quality</span>
                <span className="text-2xl font-bold text-purple-600">
                  {layoutRecommendations?.overallScore}/10
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(layoutRecommendations?.overallScore / 10) * 100}%` }}
                />
              </div>
            </div>

            {/* Recommended Pattern */}
            {layoutRecommendations?.layoutPattern && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900 mb-1">Recommended Pattern</p>
                    <p className="text-sm text-green-800">{layoutRecommendations?.layoutPattern}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Strengths */}
            {layoutRecommendations?.strengths?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  What's Working Well
                </h4>
                {layoutRecommendations?.strengths?.map((strength, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-green-600">✓</span>
                    <p className="text-sm text-gray-700 flex-1">{strength}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Improvements */}
            {layoutRecommendations?.improvements?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Improvement Opportunities
                </h4>
                {layoutRecommendations?.improvements?.map((improvement, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <h5 className="text-sm font-semibold text-gray-900 flex-1">
                        {improvement?.area}
                      </h5>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getImpactColor(improvement?.impact)}`}>
                        {improvement?.impact} impact
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{improvement?.suggestion}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && !error && !suggestions && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Sparkles className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-sm text-gray-600 mb-1">No suggestions yet</p>
            <p className="text-xs text-gray-500">Add components to get AI recommendations</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AISuggestionsPanel;